<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed, onMounted, ref, watch } from 'vue';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportsAPI from 'dashboard/api/reports';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';
import { getLineChartFilters } from 'dashboard/helper/reportTimeRangeHelper';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
  botSummary: {
    type: Object,
    default: () => ({}),
  },
  isSummaryLoading: {
    type: Boolean,
    default: false,
  },
});

const chartReportData = ref({});
const isLoadingChart = ref(false);
const isLoadingMetrics = ref(false);
const botMetrics = ref({
  conversation_count: 0,
  message_count: 0,
  resolution_rate: 0,
  handoff_rate: 0,
});
const metrics = computed(() => [
  {
    label: 'Nº de conversas',
    value: botMetrics.value.conversation_count,
    description: 'Total de conversas iniciadas',
    icon: 'chat',
    accent: 'blue',
  },
  {
    label: 'Total de respostas',
    value: botMetrics.value.message_count,
    description: 'Mensagens enviadas pelo bot',
    icon: 'chat-multiple',
    accent: 'green',
  },
  {
    label: 'Taxa de resolução',
    value: `${botMetrics.value.resolution_rate}%`,
    description: 'Conversas resolvidas pelo bot',
    icon: 'checkmark-circle',
    accent: 'violet',
  },
  {
    label: 'Taxa de transferência',
    value: `${botMetrics.value.handoff_rate}%`,
    description: 'Conversas transferidas para agentes',
    icon: 'arrow-redo',
    accent: 'amber',
  },
]);

const resolutions = computed(() => props.botSummary.bot_resolutions_count || 0);
const handoffs = computed(() => props.botSummary.bot_handoffs_count || 0);
const inProgress = computed(() =>
  Math.max(
    botMetrics.value.conversation_count - resolutions.value - handoffs.value,
    0
  )
);

const distributionRows = computed(() => [
  { label: 'Resolvidas', value: resolutions.value, color: '#44CE4B' },
  { label: 'Transferidas', value: handoffs.value, color: '#facc15' },
  { label: 'Em andamento', value: inProgress.value, color: '#1f93ff' },
]);

const distributionTotal = computed(() =>
  distributionRows.value.reduce((total, row) => total + row.value, 0)
);

const labels = computed(() => {
  const series = [
    chartReportData.value.bot_resolutions_count,
    chartReportData.value.bot_handoffs_count,
  ].find(items => items?.length);

  if (!series) return [];
  return series.map(item => format(fromUnixTime(item.timestamp), 'dd MMM'));
});

const valueCollection = key =>
  (chartReportData.value[key] || []).map(item => item.value || item.count || 0);

const dataset = (label, key, color) => ({
  label,
  data: valueCollection(key),
  borderColor: color,
  backgroundColor: color,
});

const chartCollection = computed(() => ({
  labels: labels.value,
  datasets: [
    dataset('Resoluções', 'bot_resolutions_count', '#44CE4B'),
    dataset('Transferências', 'bot_handoffs_count', '#1f93ff'),
  ],
}));

const accentClass = accent => {
  const accents = {
    blue: 'bg-woot-50 text-woot-600 dark:bg-woot-900/40 dark:text-woot-400',
    green:
      'bg-green-50 text-green-600 dark:bg-green-900/40 dark:text-green-400',
    violet:
      'bg-violet-50 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400',
    amber:
      'bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400',
  };
  return accents[accent] || accents.blue;
};

const fetchMetrics = async () => {
  const { from, to, conversationDirection, comunicanteCampaign } =
    props.filters;
  if (!from || !to || !conversationDirection) return;

  isLoadingMetrics.value = true;
  try {
    const response = await ReportsAPI.getBotMetrics({
      from,
      to,
      conversationDirection,
      comunicanteCampaign,
    });
    botMetrics.value = response.data;
  } catch {
    botMetrics.value = {
      conversation_count: 0,
      message_count: 0,
      resolution_rate: 0,
      handoff_rate: 0,
    };
  } finally {
    isLoadingMetrics.value = false;
  }
};

const fetchChartData = async () => {
  const { from, to, groupBy, conversationDirection, comunicanteCampaign } =
    getLineChartFilters(props.filters);
  if (!from || !to) return;

  const metricKeys = ['bot_resolutions_count', 'bot_handoffs_count'];

  isLoadingChart.value = true;
  try {
    const responses = await Promise.all(
      metricKeys.map(metric =>
        ReportsAPI.getReports({
          metric,
          from,
          to,
          groupBy: groupBy || 'day',
          conversationDirection,
          comunicanteCampaign,
        })
      )
    );
    chartReportData.value = metricKeys.reduce((data, metric, index) => {
      data[metric] = responses[index].data || [];
      return data;
    }, {});
  } catch {
    chartReportData.value = {};
  } finally {
    isLoadingChart.value = false;
  }
};

watch(() => props.filters, fetchMetrics, { deep: true });
watch(() => props.filters, fetchChartData, { deep: true });

onMounted(() => {
  fetchMetrics();
  fetchChartData();
});
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <ReportCard
        v-for="metric in metrics"
        :key="metric.label"
        :is-loading="isLoadingMetrics"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p
              class="mb-3 text-sm font-medium text-slate-600 dark:text-slate-300"
            >
              {{ metric.label }}
            </p>
            <strong class="text-4xl text-slate-900 dark:text-slate-50">
              {{ metric.value }}
            </strong>
            <p class="mt-3 mb-0 text-sm text-slate-500 dark:text-slate-400">
              {{ metric.description }}
            </p>
          </div>
          <span class="bot-metric-icon" :class="accentClass(metric.accent)">
            <fluent-icon :icon="metric.icon" size="24" />
          </span>
        </div>
      </ReportCard>
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard
        class="xl:col-span-5"
        :is-loading="isLoadingMetrics || isSummaryLoading"
      >
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Resumo de performance
        </h3>
        <p class="text-sm leading-6 text-slate-700 dark:text-slate-200">
          No período selecionado, o bot iniciou
          {{ botMetrics.conversation_count }} conversas e enviou
          {{ botMetrics.message_count }} respostas.
        </p>
        <p class="mb-0 text-sm leading-6 text-slate-700 dark:text-slate-200">
          {{ resolutions }} conversas foram resolvidas pelo bot e {{ handoffs }}
          foram transferidas para agentes.
        </p>
      </ReportCard>

      <ReportCard
        class="xl:col-span-3"
        :is-loading="isLoadingMetrics || isSummaryLoading"
      >
        <h3 class="mb-4 text-lg font-medium text-slate-900 dark:text-slate-50">
          Distribuição de resultados
        </h3>
        <div class="flex items-center gap-5">
          <div
            class="flex items-center justify-center w-28 h-28 rounded-full"
            :style="{
              background: `conic-gradient(#44CE4B 0 ${(resolutions / Math.max(distributionTotal, 1)) * 360}deg, #facc15 ${(resolutions / Math.max(distributionTotal, 1)) * 360}deg ${((resolutions + handoffs) / Math.max(distributionTotal, 1)) * 360}deg, #1f93ff ${((resolutions + handoffs) / Math.max(distributionTotal, 1)) * 360}deg 360deg)`,
            }"
          >
            <span
              class="flex flex-col items-center justify-center w-20 h-20 rounded-full bg-white dark:bg-slate-800"
            >
              <strong class="text-2xl text-slate-900 dark:text-slate-50">
                {{ distributionTotal }}
              </strong>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                Total
              </span>
            </span>
          </div>
          <div class="space-y-2 text-sm">
            <div
              v-for="row in distributionRows"
              :key="row.label"
              class="flex items-center justify-between gap-4"
            >
              <span
                class="flex items-center gap-2 text-slate-600 dark:text-slate-300"
              >
                <span
                  class="w-2.5 h-2.5 rounded-full"
                  :style="{ backgroundColor: row.color }"
                />
                {{ row.label }}
              </span>
              <span class="text-slate-900 dark:text-slate-50">{{
                row.value
              }}</span>
            </div>
          </div>
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-4" :is-loading="isLoadingChart">
        <div class="mb-4">
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Evolução do período
          </h3>
        </div>
        <div v-if="labels.length" class="w-full h-52">
          <ReportLineChart :collection="chartCollection" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-52 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem dados suficientes para o gráfico.
        </div>
      </ReportCard>
    </div>

    <ReportCard :is-loading="isLoadingMetrics || isSummaryLoading">
      <div class="flex items-center justify-between gap-4 mb-4">
        <div>
          <h3
            class="mb-1 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Indicadores do bot
          </h3>
          <p class="mb-0 text-sm text-slate-500 dark:text-slate-400">
            Acompanhamento das métricas disponíveis para o período selecionado.
          </p>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="p-4 rounded-md bg-slate-25 dark:bg-slate-900">
          <span class="text-sm text-slate-500 dark:text-slate-400">
            Resoluções pelo bot
          </span>
          <strong
            class="block mt-2 text-3xl text-green-600 dark:text-green-400"
          >
            {{ resolutions }}
          </strong>
        </div>
        <div class="p-4 rounded-md bg-slate-25 dark:bg-slate-900">
          <span class="text-sm text-slate-500 dark:text-slate-400">
            Transferências para agentes
          </span>
          <strong class="block mt-2 text-3xl text-woot-600 dark:text-woot-400">
            {{ handoffs }}
          </strong>
        </div>
      </div>
    </ReportCard>
  </div>
</template>

<style lang="scss" scoped>
.bot-metric-icon {
  @apply flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full;
}
</style>
